import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, of, Subject, switchMap } from 'rxjs';
import { FilterOperators } from 'src/app/common/enums';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { UserService } from 'src/app/data/user.service';
import { FilterType } from 'src/app/list-filter/enums/filter-type.enum';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { DictionaryItem } from '../../models/dictionary-item';

@Injectable()
export class LegalizationListFilterService extends FilterService {
    protected userService: UserService;
    private translateService = inject(TranslateService);
    private dictionaryService = inject(DictionaryService);

    protected filterType = FilterTypeEnum.LegalizationsList;
    private mpks$ = new BehaviorSubject<DictionaryItem[]>(null);
    private subject: Subject<string> = new Subject<string>();

    constructor() {
        const userService = inject(UserService);

        super(FilterTypeEnum.LegalizationsList, userService);
        this.userService = userService;


        this.config = [
            {
                id: 1,
                name: 'firstName',
                label: this.translateService.instant('FirstName'),
                property: 'FirstName',
                operator: FilterOperators.Contains,
                default: true,
                type: FilterType.Text,
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 2,
                name: 'lastName',
                label: this.translateService.instant('LastName'),
                property: 'LastName',
                operator: FilterOperators.Contains,
                default: true,
                type: FilterType.Text,
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 3,
                name: 'pesel',
                label: this.translateService.instant('Pesel'),
                property: 'Pesel',
                default: true,
                operator: FilterOperators.StringEqual,
                type: FilterType.Text,
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 4,
                name: 'nip',
                label: this.translateService.instant('Nip'),
                property: 'Nip',
                default: true,
                operator: FilterOperators.StringEqual,
                type: FilterType.Text,
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 5,
                name: 'legalizationStatusId',
                label: this.translateService.instant('Legalization.LegalizationStatus'),
                property: 'LegalizationStatusId',
                operator: FilterOperators.Equal,
                multiple: true,
                default: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getLegalizationStatuses(true),
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 6,
                name: 'isCurrentProcess',
                label: this.translateService.instant('Legalization.Register.CurrentProcesses'),
                property: 'IsCurrentProcess',
                operator: FilterOperators.Equal,
                default: true,
                datasource$: this.booleanFilter(),
                type: FilterType.Select,
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 7,
                name: 'companyId',
                label: this.translateService.instant('Company'),
                property: 'CompanyId',
                operator: FilterOperators.Equal,
                type: FilterType.Autocomplete,
                default: true,
                datasource$: this.subject.asObservable().pipe(
                    switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
                ),
                searchTextChangeCallback: (value: any) => this.subject.next(value),
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 8,
                name: 'employerId',
                label: this.translateService.instant('Employer'),
                property: 'EmployerId',
                operator: FilterOperators.Equal,
                default: true,
                type: FilterType.Autocomplete,
                datasource$: this.subject.asObservable().pipe(
                    switchMap((value: string) => (value ? this.dictionaryService.getEmployers(value) : of([]))),
                ),
                searchTextChangeCallback: (value: any) => this.subject.next(value),
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 9,
                name: 'employerObjectId',
                label: this.translateService.instant('EmployerObject'),
                property: 'EmployerObjectId',
                operator: FilterOperators.Equal,
                default: true,
                type: FilterType.SearchableMultiselect,
                getData: (value: string, page: number, pageSize: number) => this.dictionaryService.getEmployerObjectsPaged(value, page, pageSize),
                changeCallback: (value: any[]) => this.mpks$.next(value?.map(item => ({ Id: item.Id, Name: item.Mpk })) ?? null),
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 10,
                name: 'agreementTypeId',
                label: this.translateService.instant('AgreementType'),
                property: 'AgreementTypeId',
                operator: FilterOperators.Equal,
                default: true,
                multiple: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getAgreementTypes(),
                searchTextChangeCallback: (value: any) => this.subject.next(value),
                description: this.translateService.instant('Legalization.Register.LegalizationsProcesses')
            },
            {
                id: 11,
                name: 'documentTypeId',
                label: this.translateService.instant('Legalization.DocumentType'),
                property: 'DocumentTypeId',
                operator: FilterOperators.Equal,
                multiple: true,
                default: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getLegalizationDocumentFileTypes(),
                description: this.translateService.instant('Legalization.Register.Documents')
            },
            {
                id: 12,
                name: 'documentStatusId',
                label: this.translateService.instant('Legalization.Register.DocumentStatusName'),
                property: 'DocumentStatusId',
                operator: FilterOperators.Equal,
                multiple: true,
                default: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getLegalizationDocumentStatuses(true),
                description: this.translateService.instant('Legalization.Register.Documents')
            },
            {
                id: 13,
                name: 'documentAlertStatusId',
                label: this.translateService.instant('Legalization.Register.AlertStatus'),
                property: 'DocumentAlertStatusId',
                operator: FilterOperators.Equal,
                multiple: true,
                default: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getLegalizationAlertStatuses(),
                description: this.translateService.instant('Legalization.Register.Documents')
            },
            {
                id: 14,
                name: 'legalizationDocumentCategoryId',
                label: this.translateService.instant('Legalization.Register.DocumentCategoryName'),
                property: 'LegalizationDocumentCategoryId',
                operator: FilterOperators.Equal,
                multiple: true,
                default: true,
                type: FilterType.Select,
                datasource$: this.dictionaryService.getLegalizationDocumentCategories(),
                description: this.translateService.instant('Legalization.Register.Documents')
            }
        ]
    }
}
